import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import SetCard from "@/components/SetCard";
import { ArrowRight, Sparkles } from "lucide-react";

/* ---------------------------------------------------------------------------
 * Atmospheric Spreads — four large, hi-res scenes that anchor the landing as
 * a visual journal rather than a product feed. Each spread carries its own
 * mood label + one-line caption.
 *
 * Imagery is sourced from Unsplash (license-free, hot-linked for now). Once
 * curators publish more sets of their own, the spreads should be replaced
 * with curator-authored boards (TODO).
 * ------------------------------------------------------------------------- */
const ATMOSPHERIC_SPREADS = [
  {
    key: "moody_reading_nook",
    mood: "Moody Reading Nook",
    caption: "A single armchair, a low lamp, the hush of a Sunday afternoon.",
    img: "https://images.unsplash.com/photo-1493663284031-b7e3aefcae8e?w=1600&q=85&auto=format&fit=crop",
  },
  {
    key: "european_sunday",
    mood: "European Sunday",
    caption: "Tall windows, parquet floors, fresh bread on the table.",
    img: "https://images.unsplash.com/photo-1505691938895-1758d7feb511?w=1600&q=85&auto=format&fit=crop",
  },
  {
    key: "quiet_luxury_living",
    mood: "Quiet Luxury Living",
    caption: "Bouclé, brass, plaster — a living room that whispers.",
    img: "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?w=1600&q=85&auto=format&fit=crop",
  },
  {
    key: "earthy_textures",
    mood: "Earthy Textures",
    caption: "Clay, oak, plaster and weave — texture as the only ornament.",
    img: "https://images.unsplash.com/photo-1567016526105-22da7c13161a?w=1600&q=85&auto=format&fit=crop",
  },
];

// (older inline override block removed — single source of truth above)

const CURRENT_MOODS = [
  "Quiet Luxury",
  "Moody Corners",
  "Earth & Linen",
  "Sunday Spaces",
  "Minimalist Bath",
  "Sun-drenched Kitchen",
];

export default function Landing() {
  const nav = useNavigate();
  const [sets, setSets] = useState([]);
  const [moodFilter, setMoodFilter] = useState("");

  useEffect(() => {
    let cancelled = false;
    const fetchSets = async (retry = 0) => {
      try {
        const params = new URLSearchParams();
        // Homepage feed is locked to home-aesthetic content only: interior +
        // design_elements categories (rooms + textures + visual building blocks).
        // Fashion sets are excluded by design — this is "Romanticize Home".
        params.set("category", "interior,design_elements");
        if (moodFilter) params.set("q", moodFilter);
        params.set("limit", "24");
        const { data } = await api.get(`/sets?${params.toString()}`);
        if (!cancelled) setSets(data || []);
      } catch (e) {
        if (!cancelled && retry < 2) setTimeout(() => fetchSets(retry + 1), 600 * (retry + 1));
      }
    };
    const t = setTimeout(fetchSets, 150);
    return () => { cancelled = true; clearTimeout(t); };
  }, [moodFilter]);

  return (
    <div className="bg-transparent">
      {/* ---- Editorial Hero ---- */}
      <section className="relative">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 pt-16 pb-10 lg:pt-24 lg:pb-16 text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#9CA47E] mb-5 inline-flex items-center gap-2 justify-center">
            <Sparkles className="w-3 h-3" /> Issue No. 01 · The Visual Journal
          </div>
          <h1
            data-testid="hero-brand"
            className="font-heading text-6xl sm:text-7xl lg:text-8xl leading-[0.95] text-[#2C2A26]"
            style={{ letterSpacing: "-0.022em" }}
          >
            VoraStyle
          </h1>
          <p
            data-testid="hero-slogan"
            className="font-heading text-2xl sm:text-3xl lg:text-[32px] text-[#3A352F] mt-7 max-w-3xl mx-auto leading-[1.22]"
            style={{ fontWeight: 400, letterSpacing: "-0.005em" }}
          >
            Curated interiors for people who <span className="italic" style={{ fontWeight: 300 }}>romanticize home.</span>
          </p>
          <p
            data-testid="hero-secondary"
            className="font-body text-[15px] sm:text-base text-[#5C5C5C] mt-5 max-w-xl mx-auto leading-relaxed"
          >
            Aesthetic living, palettes, and visual inspiration.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-9">
            <button
              data-testid="hero-cta-primary"
              onClick={() => nav("/register")}
              className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#9CA47E] hover:bg-[#7E876A] text-[#F2EDE3] font-mono text-[12px] uppercase tracking-[0.22em] transition-colors"
            >
              Begin the Story <ArrowRight className="w-3.5 h-3.5" />
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => nav("/editor")}
              className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#CFC8B6] hover:bg-[#CFC8C0] text-[#333333] font-mono text-[12px] uppercase tracking-[0.22em] transition-colors"
            >
              Open The Studio
            </button>
          </div>
        </div>
      </section>

      {/* ---- The Curator · onboarding ---- */}
      <section className="bg-[#F2EDE3]" data-testid="curator-onboarding">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-16 md:py-20 grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-10 md:gap-16 items-center">
          {/* Abstract V */}
          <div className="hidden md:flex items-center justify-center w-28 h-28 border border-[#E7E3DC] bg-white">
            <svg viewBox="0 0 60 60" aria-hidden="true" className="w-14 h-14 text-[#9CA47E]">
              <path d="M8 12 L30 50 L52 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M18 12 L30 33 L42 12" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" opacity="0.5" />
            </svg>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#9CA47E] mb-4">
              ◇ Meet <span className="italic font-heading text-[12px] tracking-tight normal-case">The Curator</span> · Save inspiration
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-[#333333] leading-[1.1] max-w-2xl">
              Build your own private archive.
            </h2>
            <p className="font-body text-[15px] sm:text-base text-[#5C5C5C] mt-4 max-w-2xl leading-relaxed">
              Save textures, lighting, and gathered pieces from any corner of the web directly into your Studio. The Curator clips an image, names it, and files it under a chapter — quietly, in the background.
            </p>
          </div>
          <Link
            to="/products"
            data-testid="curator-onboarding-cta"
            className="self-end md:self-center inline-flex items-center gap-2 px-6 py-3.5 bg-[#333333] hover:bg-[#1F1F1F] text-[#F2EDE3] font-mono text-[11px] uppercase tracking-[0.22em] transition-colors whitespace-nowrap"
          >
            Open The Curator <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </section>

      {/* ---- Current Moods ---- */}
      <section className="border-y border-[#E7E3DC] bg-[#EFEAE2]">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-7 flex flex-col md:flex-row md:items-center gap-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#9CA47E] shrink-0">
            § Current Moods
          </div>
          <div className="flex flex-wrap gap-2" data-testid="current-moods">
            {CURRENT_MOODS.map((m) => {
              const active = moodFilter === m;
              return (
                <button
                  key={m}
                  type="button"
                  data-testid={`mood-${m.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setMoodFilter(active ? "" : m)}
                  className={`px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] border transition-colors cursor-pointer ${
                    active
                      ? "bg-[#9CA47E] text-[#F2EDE3] border-[#9CA47E]"
                      : "bg-[#F2EDE3] text-[#333333] border-[#CFC8B6] hover:border-[#9CA47E] hover:text-[#9CA47E]"
                  }`}
                >
                  {m}
                </button>
              );
            })}
            {moodFilter && (
              <button
                type="button"
                data-testid="mood-clear"
                onClick={() => setMoodFilter("")}
                className="px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#9CA47E] underline-offset-4 underline cursor-pointer"
              >
                clear
              </button>
            )}
          </div>
        </div>
      </section>

      {/* ---- Three Quiet Promises ---- */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 border-b border-[#DDD4C2]" data-testid="three-promises">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16">
          {[
            { eyebrow: "i.", title: "Curated, not algorithmic.", body: "Every spread is composed by a human eye. No infinite scroll, no engagement-bait — just rooms that breathe." },
            { eyebrow: "ii.", title: "A studio for slow taste.", body: "Built for the curator who lingers. Nothing here rushes you toward a 'done' button." },
            { eyebrow: "iii.", title: "For people who romanticize home.", body: "Linen sheets, late-afternoon light, a single ceramic vase. Slow taste over fast trends." },
          ].map((p) => (
            <div key={p.title} className="space-y-3">
              <div className="font-heading italic text-2xl text-[#9CA47E]">{p.eyebrow}</div>
              <div className="font-heading text-xl tracking-tight text-[#333333] leading-tight">{p.title}</div>
              <div className="font-body text-[14px] text-[#5C5C5C] leading-relaxed">{p.body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ---- Atmospheric Spreads ---- */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-10 pt-16 pb-10">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#9CA47E] mb-3">
              § Atmospheric Spreads
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-[#333333] leading-[1.05]">
              Four rooms,<br /> four ways to slow down.
            </h2>
          </div>
          <Link
            to="/discover"
            data-testid="spreads-cta"
            className="hidden md:inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.22em] text-[#9CA47E] hover:text-[#7E876A]"
          >
            Wander the journal <ArrowRight className="w-3 h-3" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8" data-testid="atmospheric-spreads">
          {ATMOSPHERIC_SPREADS.map((s, i) => (
            <article
              key={s.key}
              data-testid={`spread-${s.key}`}
              className={`group relative overflow-hidden bg-[#EFEAE2] cursor-pointer ${i % 3 === 0 ? "md:row-span-1" : ""}`}
              onClick={() => { setMoodFilter(s.mood); window.scrollTo({ top: document.getElementById("journal-feed")?.offsetTop || 0, behavior: "smooth" }); }}
            >
              <div className="aspect-[4/5] md:aspect-[5/6] overflow-hidden">
                <img
                  src={s.img}
                  alt={s.mood}
                  loading={i < 2 ? "eager" : "lazy"}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#333333]/65 via-[#333333]/15 to-transparent pointer-events-none" />
              <div className="absolute left-6 right-6 bottom-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#CFC8B6] mb-2">
                  No. {String(i + 1).padStart(2, "0")} · {s.mood}
                </div>
                <p className="font-heading text-2xl sm:text-3xl text-[#F2EDE3] leading-tight tracking-tight max-w-md">
                  {s.caption}
                </p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* ---- Quiet manifesto ---- */}
      <section className="border-y border-[#E7E3DC] bg-[#EFEAE2]">
        <div className="max-w-[920px] mx-auto px-6 lg:px-10 py-20 text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#9CA47E] mb-5">
            ◇ The Manifesto
          </div>
          <p className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight text-[#333333] leading-[1.2]">
            We believe a room is an argument — about light, about time, about the kind of life you want.
            Collect the things that argue well.
          </p>
        </div>
      </section>

      {/* ---- Palette of the Week ---- */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20" data-testid="palette-of-week">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#9CA47E] mb-3">§ Palette of the Week</div>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-[#333333] leading-[1.05]">
              Quiet Saturday<br /><span className="italic">at Soane Museum</span>
            </h2>
            <p className="font-body text-[14px] text-[#5C5C5C] mt-4 max-w-md leading-relaxed">
              Lime-washed plaster, oxblood velvet, brass patina. A palette borrowed from the candlelit corners of a London drawing room.
            </p>
          </div>
          <Link
            to="/editor"
            data-testid="palette-cta"
            className="self-start md:self-end inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.22em] text-[#9CA47E] hover:text-[#7E876A]"
          >
            Use this palette <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        <div className="grid grid-cols-5 gap-0 border border-[#DDD4C2]" data-testid="palette-swatches">
          {[
            { hex: "#E8DDC9", name: "Lime-washed Plaster" },
            { hex: "#9B5547", name: "Oxblood Velvet" },
            { hex: "#2E2A24", name: "Soot" },
            { hex: "#B89B6E", name: "Brass Patina" },
            { hex: "#6E6657", name: "Charred Oak" },
          ].map((c) => (
            <div key={c.hex} className="aspect-square flex flex-col items-start justify-end p-4 transition-transform hover:scale-[1.02] cursor-default" style={{ backgroundColor: c.hex }}>
              <div className={`font-mono text-[10px] uppercase tracking-[0.18em] ${["#2E2A24","#9B5547","#6E6657"].includes(c.hex) ? "text-white/90" : "text-[#2C2A26]/80"}`}>{c.hex}</div>
              <div className={`font-heading text-[13px] italic mt-1 ${["#2E2A24","#9B5547","#6E6657"].includes(c.hex) ? "text-white" : "text-[#2C2A26]"} leading-tight`}>{c.name}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ---- From the Atelier — featured spreads ---- */}
      <section className="bg-[#2C2A26] text-[#F2EDE3]" data-testid="from-the-atelier">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-10">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#CFC8B6] mb-3">§ From the Atelier</div>
              <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-[1.05]">Spreads we keep<br /> coming back to.</h2>
            </div>
            <Link
              to="/discover"
              data-testid="atelier-cta"
              className="self-start md:self-end inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.22em] text-[#CFC8B6] hover:text-[#F2EDE3]"
            >
              See all curated <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          {sets.length === 0 ? (
            <div className="font-body text-[#CFC8B6] py-10 text-center">Curating the atelier…</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-10" data-testid="atelier-spreads">
              {sets.slice(0, 3).map((s, i) => (
                <Link
                  key={s.id}
                  to={`/sets/${s.id}`}
                  data-testid={`atelier-spread-${i}`}
                  className="group block"
                >
                  <div className="aspect-[4/5] overflow-hidden bg-[#3A352F] border border-[#3A352F] mb-4">
                    {s.cover_image ? (
                      <img src={s.cover_image} alt={s.title} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center font-heading italic text-3xl text-[#CFC8B6]/40">{(s.title || "—")[0]}</div>
                    )}
                  </div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.24em] text-[#9CA47E] mb-2">No. {String(i + 1).padStart(2, "0")}</div>
                  <div className="font-heading text-xl tracking-tight text-[#F2EDE3] group-hover:text-[#9CA47E] transition-colors leading-tight">{s.title || "Untitled spread"}</div>
                  <div className="font-body text-[12px] text-[#CFC8B6]/80 mt-1.5">{(s.tags || []).slice(0, 3).join(" · ") || "a quiet composition"}</div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ---- Journal feed (community sets) ---- */}
      <section id="journal-feed" className="max-w-[1280px] mx-auto px-6 lg:px-10 py-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-8">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#9CA47E] mb-3">
              § From the Collective
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-[#333333] leading-[1.05]">
              {moodFilter ? <>Sets tagged <span className="italic">{moodFilter.toLowerCase()}</span></> : "Latest journal entries"}
            </h2>
          </div>
          {moodFilter && (
            <button
              type="button"
              data-testid="feed-reset"
              onClick={() => setMoodFilter("")}
              className="self-start md:self-end font-mono text-[10px] uppercase tracking-[0.22em] text-[#9CA47E] hover:text-[#7E876A] cursor-pointer"
            >
              show all
            </button>
          )}
        </div>

        {sets.length === 0 ? (
          <div className="font-body text-[#5C5C5C] py-16 text-center">
            {moodFilter ? "No sets yet under this mood — be the first to publish one." : "Loading the journal…"}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {sets.slice(0, 12).map((s) => <SetCard key={s.id} set={s} hidePalette />)}
          </div>
        )}
      </section>

      {/* ---- Big CTA ---- */}
      <section className="bg-[#333333] text-[#F2EDE3]">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 md:grid-cols-[1.4fr_1fr] gap-10 items-end">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.32em] text-[#CFC8B6] mb-4">◇ The Collective</div>
            <h2 className="font-heading text-4xl sm:text-5xl tracking-tight leading-[1]">
              Curate the world<br /> the way <span className="italic">you</span> see it.
            </h2>
            <p className="font-body text-[#CFC8B6]/85 mt-5 max-w-md leading-relaxed">
              An open community of curators making slow, beautiful sets together. Bring your eye, share your edits, find your people.
            </p>
          </div>
          <div className="flex flex-col gap-3 max-w-sm">
            <button
              onClick={() => nav("/register")}
              data-testid="cta-join"
              className="px-5 py-4 bg-[#9CA47E] hover:bg-[#7E876A] text-[#F2EDE3] font-mono text-[12px] uppercase tracking-[0.22em] inline-flex items-center justify-center gap-2"
            >
              Join the collective <ArrowRight className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => nav("/editor")}
              className="px-5 py-4 bg-transparent border border-[#CFC8B6] text-[#F2EDE3] hover:bg-[#CFC8B6] hover:text-[#333333] font-mono text-[12px] uppercase tracking-[0.22em] transition-colors"
            >
              Open The Studio
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
